/** The answer from our own edge Worker, as far as the apps read it. */
type IpLookupResponse = {
  country?: string | null;
  postalCode?: string | null;
};

export type IpLocation = {
  /** ISO-3166 alpha-2, or null when the edge could not place the address. */
  country: string | null;
  postalCode: string | null;
};

/**
 * Our Worker at louise-life/iplookup-worker. It reads Cloudflare's edge
 * geolocation for the connecting IP and answers as JSON; no third party sees
 * the request and nothing is stored.
 */
const IP_COUNTRY_LOOKUP_URL = "https://iplookup.louise.life/";

/** A slow network must not hold a default back. */
export const IP_COUNTRY_LOOKUP_TIMEOUT_MS = 3000;

/**
 * Where the visitor's address seems to be, without persisting the response.
 * Callers should use this only where a browser/server IP signal is available
 * and keep a local fallback for native or unavailable-network paths.
 */
export async function getIpLocation(
  signal?: AbortSignal,
): Promise<IpLocation | null> {
  try {
    const response = await fetch(IP_COUNTRY_LOOKUP_URL, { signal });
    if (!response.ok) return null;

    const payload = (await response.json()) as IpLookupResponse;
    const countryCode =
      typeof payload.country === "string"
        ? payload.country.trim().toUpperCase()
        : "";
    return {
      country: /^[A-Z]{2}$/.test(countryCode) ? countryCode : null,
      postalCode:
        typeof payload.postalCode === "string" && payload.postalCode.trim()
          ? payload.postalCode.trim()
          : null,
    };
  } catch {
    return null;
  }
}

/** The visitor's ISO country code, or null when the edge could not place it. */
export async function getIpCountryCode(
  signal?: AbortSignal,
): Promise<string | null> {
  const location = await getIpLocation(signal);
  return location?.country ?? null;
}

/**
 * The lookup with a deadline. Past `timeoutMs`, or when the caller aborts,
 * it answers null and the caller falls through to the device region. The
 * lookup itself is injectable so the deadline can be tested without a
 * network.
 */
export async function getIpCountryCodeWithTimeout({
  timeoutMs = IP_COUNTRY_LOOKUP_TIMEOUT_MS,
  signal,
  lookup = getIpCountryCode,
}: {
  timeoutMs?: number;
  signal?: AbortSignal;
  lookup?: (signal: AbortSignal) => Promise<string | null>;
} = {}): Promise<string | null> {
  if (signal?.aborted) return null;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  const abortFromCaller = () => controller.abort();
  signal?.addEventListener("abort", abortFromCaller);
  try {
    return await lookup(controller.signal);
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abortFromCaller);
  }
}
